import React from "react"
import { Dialog, FlatButton } from "material-ui"
import AuthenticationStore from "../../stores/AuthenticationStore"
import AccountStore from "../../stores/AccountStore"
import strings from "../../localization/strings"
import ChooseHierarchyJobPhaseChipForPunch from "../../components/hierarchy/choose_hierarchy/ChooseHierarchyJobPhaseChipForPunch"
import EquipmentChip from "../equipment/EquipmentChip"
import autobind from "class-autobind"
import moment from "moment-timezone"
import * as utils from "../../helpers/utils"
import * as timeHelper from "../../helpers/timeHelper"
import * as api from "../../api"
import { IDate, IPunchDocument, IEquipmentDocument, IJobDocument, IJobPhase, IPosition, IPunchBundle, IUIUser, ITimecardEmployee } from "../../models"
import * as punchHelper from "../../helpers/punchHelper"
import Position_PayType_PayClass_Selector from "../position_paytype_payclass_selector/Position_PayType_PayClass_Selector"
import AppStateStore from "../../stores/AppStateStore"
import * as userHelper from "../../helpers/userHelper"
import PunchDateTimeSelector from "./PunchDateTimeSelector"
import GlobalPunchEmployeesInfoHoverIcon from "./global_punch/GlobalPunchEmployeesInfoHoverIcon"
import { checkJobFieldAvailable } from "../../helpers/jobHelper"
interface IProps {
	isPunchIn: boolean
	pairedPunchId: string
	date: IDate
	hierarchyId: string
	user: IUIUser
	positionId: string
	payClassId: string
	payTypeId: string
	shiftId: string
	breakId: string
	mobileBreakId: string
	jobId: string
	phaseId: string
	equipmentId: string
	payrollHierarchyId: string
	payrollPayGroupId: string
	onRequestClose(): void
	addPunch(userId: string, punchBundle: IPunchBundle): void

	//we need to select hierarchy based on employee punch permission, not logged in user punch permission (when the user has more punch rights than the admin)
	//it's possible when full timecard, for example, employee->timecard and payroll->timecard
	//timecards page is not full timecard in the case filter by punch level
	isEmployeePermissionPriority?: boolean

	//GlobalAddPunch
	isGlobalPunch?: boolean
	globalPunchEmployees?: ITimecardEmployee[]
	handleAddGlobalPunch?(punchBundle: IPunchBundle): void

	//Employee->Timecard->PunchDrawerTable
	useEditablePunchTableDataStore?: boolean
}
interface IState {
	loadingPositions: boolean
	hour: number
	minute: number
	date: any
	hierarchyLevelId: string
	errorHierarchyId: string
	job: IJobDocument
	phase: IJobPhase
	positionId: string
	punchPosition: IPosition
	errorPunchPosition: string
	selectedPayClassId: string
	selectedPayTypeId: string
	errorSelectedPayTypeId: string
	enableEquipment: boolean
	equipmentList: IEquipmentDocument[]
	hierarchyLevelIntentionalChanged: boolean
}

export default class AddPunchDialog extends React.Component<IProps, IState> {
	constructor(props: IProps) {
		super(props)
		const _moment = moment()
		this.state = {
			loadingPositions: true,
			hour: _moment.hour(),
			minute: _moment.minute(),
			date: props.date ? timeHelper.keepConvertUTCToLocal(props.date) : null,
			hierarchyLevelId: props.hierarchyId,
			errorHierarchyId: null,
			job: null,
			phase: null,
			positionId: props.positionId,
			punchPosition: null,
			errorPunchPosition: null,
			selectedPayClassId: props.payClassId ? props.payClassId : null,
			selectedPayTypeId: props.payTypeId ? props.payTypeId : null,
			errorSelectedPayTypeId: null,
			enableEquipment: AccountStore.getEquipmentEnabled(),
			equipmentList: [],
			hierarchyLevelIntentionalChanged: false
		}
		autobind(this)
	}

	componentDidMount() {
		!this.props.isGlobalPunch &&
			this.checkHierarchyIsValidForEmployee()
		document.addEventListener("keydown", this.handleReturnKey, false)
	}

	componentWillUnmount() {
		document.removeEventListener("keydown", this.handleReturnKey)
	}

	checkHierarchyIsValidForEmployee() {
		const isValid = this.props.isPunchIn ?
			userHelper.getEmployeeAccessHierarchyIds(this.props.user).includes(this.props.hierarchyId) :
			true
		if (isValid) {
			this.getJobPhase(this.props.jobId, this.props.phaseId)
		}
		else {
			this.setState({ hierarchyLevelId: this.props.user.DefaultPunchHierarchyId, positionId: this.props.user.DefaultPositionId })
			this.getJobPhase(this.props.user.DefaultJobId, this.props.user.DefaultPhaseId)
		}
	}

	getJobPhase(jobId: string, phaseId: string) {
		jobId &&
			api.getJob(jobId).then(res => {
				const job: IJobDocument = res.data
				this.setState({
					job,
					phase: phaseId ? job.Phases.find(item => item.Id === phaseId) : null
				})
			})
	}

	handleReturnKey(e) {
		e.keyCode === 13 &&
			this.handleRequestSave()
	}

	checkValidate() {
		return (this.state.errorHierarchyId || this.state.errorSelectedPayTypeId || this.state.errorPunchPosition) ? false : true
	}

	async handleRequestSave() {
		if (!this.checkValidate()) return
		const timezoneId = await timeHelper.getIANATimezoneNameFromHierarchyJobId(this.state.hierarchyLevelId, this.state.job ? this.state.job.Id : null)
		const punch: IPunchDocument = {
			OwnerUserId: AuthenticationStore.getLoggedInUser().Id,
			AccountId: AccountStore.getCurrentAccount().Id,
			DateTime: timeHelper.getTimezoneTranslatedDateTime(this.state.date, this.state.minute, this.state.hour, timezoneId),
			Offline: false,
			MetaData: {
				UploadDateTime: moment().format(),
				Latitude: 0,
				Longitude: 0,
				Accuracy: 0,
				BiometricScore: 0
			},
			ShiftId: this.props.shiftId,
			BreakId: this.props.breakId,
			MobileBreakId: this.props.mobileBreakId,
			PayTypeId: [AccountStore.DiffPayTypeId, AccountStore.EmptyPayTypeId].includes(this.state.selectedPayTypeId) ? null : this.state.selectedPayTypeId,
			PayClassId: this.state.selectedPayClassId,
			EquipmentId: this.props.isPunchIn ? (this.state.equipmentList.length > 0 ? this.state.equipmentList[0].Id : null) : this.props.equipmentId,
			JobId: this.state.job && this.state.job.Id,
			JobName: this.state.job && this.state.job.Name,
			JobCostCode: this.state.job && this.state.job.CostCode,
			PhaseId: this.state.job && this.state.phase && this.state.phase.Id,
			PhaseName: this.state.job && this.state.phase && this.state.phase.Name,
			PhaseCostCode: this.state.job && this.state.phase && this.state.phase.CostCode,
			PayrollHierarchyItemId: this.props.payrollHierarchyId ? this.props.payrollHierarchyId : null,
			PayrollPayGroupId: this.props.payrollPayGroupId ? this.props.payrollPayGroupId : null
		}

		if (this.state.hierarchyLevelId && this.state.hierarchyLevelId.length > 0) {
			punch.HierarchyItemId = this.state.hierarchyLevelId
		}
		if (this.state.positionId && this.state.positionId.length > 0) {
			punch.PositionId = this.state.positionId
		}
		const punchBundle = {
			Punch: punch,
			PunchPosition: AccountStore.getPayClassEnabled() && this.state.punchPosition ?
				{ Position: this.state.punchPosition } :
				null
		}
		const disablePuncheMessageByDefinition = punchHelper.getDisablePuncheMessageByDefinition(punch.HierarchyItemId, punch.JobId, punch.PhaseId)
		this.props.isPunchIn && disablePuncheMessageByDefinition ?
			AppStateStore.setProgressSnackbarContent(disablePuncheMessageByDefinition) :
			this.props.isGlobalPunch ?
				this.handleAddGlobalPunch(punchBundle) :
				this.handleAddPunch(punchBundle)
	}

	handleAddPunch(punchBundle: IPunchBundle) {
		punchBundle.Punch.Id = utils.guid()
		punchBundle.Punch.UserId = this.props.user.Id
		this.props.addPunch(this.props.user.Id, punchBundle)
	}

	handleAddGlobalPunch(punchBundle: IPunchBundle) {
		AppStateStore.setConfirmDialogContent({
			title: strings.Global_Add_Confirmation,
			content: strings.This_action_will_add_the_specified_punch_criteria_for_the_Selected_Employees_Are_you_sure_that_you_want_to_continue,
			confirm: () => this.props.handleAddGlobalPunch(punchBundle)
		})
	}

	render() {
		return (
			<div>
				<Dialog
					actionsContainerClassName="AddPunchDialog"
					open
					title={this.props.isGlobalPunch ?
						[
							<h3 key="title" style={{ display: "inline-block", margin: 0, padding: "24px 24px 20px", colorr: "rgb(97, 97, 97)", fontSize: 22, lineHeight: "32px", fontWeight: 400 }}>{strings.Global_Punch_Add}</h3>,
							<GlobalPunchEmployeesInfoHoverIcon
								key="info"
								style={{ display: "inline-block", marginRight: 20 }}
								globalPunchEmployees={this.props.globalPunchEmployees as IUIUser[]}
							/>
						] :
						`${strings.Add_Punch.toUpperCase()} - ${this.props.user.FirstName} ${this.props.user.LastName}`
					}
					actions={[
						<FlatButton data-intercom-target={"Btn-Close-AddPunch-Dialog"} key="Cancel" label={strings.CANCEL} secondary onClick={this.props.onRequestClose} />,
						<FlatButton data-intercom-target={"Btn-AddPunch"} key="Punch" label={strings.Add_Punch.toUpperCase()} primary disabled={this.state.loadingPositions} onClick={this.handleRequestSave} />
					]}
				>
					<div>
						<ChooseHierarchyJobPhaseChipForPunch
							hideIcon={false}
							readonly={!this.props.isPunchIn}
							hierarchyChipLabel={strings.ChoosePunchHierarchy}
							jobChipLabel={checkJobFieldAvailable('singular') ? `Choose ${AccountStore.getCurrentAccountSettings().CustomFieldSettings.JobFieldLabels.SingularJobLabel}` : strings.Choose_Job}
							phaseChipLabel={ punchHelper.checkPhaseFieldAvailable("singular") ? `Choose ${AccountStore.getCurrentAccountSettings().CustomFieldSettings.PhaseFieldLabels.SingularPhaseLabel}`: strings.Choose_Phase}
							employee={this.props.user as IUIUser}
							punchHierarchyItemId={this.state.hierarchyLevelId}
							jobId={this.state.job ? this.state.job.Id : null}
							phaseId={this.state.phase ? this.state.phase.Id : null}
							handleSelectHierarchyJobPhase={(hierarchyLevelId, errorHierarchyId, job, phase, intentionalChanged) =>
								this.setState({
									hierarchyLevelId,
									errorHierarchyId,
									job,
									phase,
									hierarchyLevelIntentionalChanged: intentionalChanged ? true : this.state.hierarchyLevelIntentionalChanged
								})
							}
							isEmployeePermissionPriority={this.props.isEmployeePermissionPriority}
							isGlobalPunch={this.props.isGlobalPunch}
							globalPunchEmployees={this.props.globalPunchEmployees as IUIUser[]}
							useEditablePunchTableDataStore={this.props.useEditablePunchTableDataStore}
							searchJobByEmpPermission={this.props.isGlobalPunch ? false : true}
						/>
						<Position_PayType_PayClass_Selector
							readonly={!this.props.isPunchIn}
							date={this.state.date}
							employee={this.props.user as IUIUser}
							hierarchyId={this.state.hierarchyLevelId}
							shiftId={this.props.shiftId}
							jobId={this.state.job ? this.state.job.Id : null}
							phaseId={this.state.phase ? this.state.phase.Id : null}
							selectedPositionId={this.state.positionId}
							selectedPayTypeId={this.state.selectedPayTypeId}
							selectedPayClassId={this.state.selectedPayClassId}
							keepPropsPayClassId_PayTypeId={!this.props.isPunchIn}
							handleLoadingPositions={loadingPositions => this.setState({ loadingPositions })}
							handleSelectPosition={(position, errorPunchPosition) => this.setState({ punchPosition: position, positionId: position ? position.Id : null, errorPunchPosition })}
							handleSelectPayClass={selectedPayClassId => this.setState({ selectedPayClassId })}
							handleSelectPayType={(selectedPayTypeId, errorSelectedPayTypeId) => this.setState({ selectedPayTypeId, errorSelectedPayTypeId })}
							referencePunchPositionDocumentId={this.props.pairedPunchId}
							isGlobalPunch={this.props.isGlobalPunch}
							globalPunchEmployees={this.props.globalPunchEmployees as IUIUser[]}
							useEditablePunchTableDataStore={this.props.useEditablePunchTableDataStore}
							hierarchyLevelIntentionalChanged={this.state.hierarchyLevelIntentionalChanged}
						/>
						<PunchDateTimeSelector
							hierarchyId={this.state.hierarchyLevelId}
							jobId={this.state.job ? this.state.job.Id : null}
							date={this.state.date}
							hour={this.state.hour}
							minute={this.state.minute}
							handleChange={(date, hour, minute) => this.setState({ date, hour, minute })}
						/>
						{this.state.enableEquipment && (this.props.isPunchIn || this.props.equipmentId) &&
							<div style={{ marginTop: 5 }}>
								<EquipmentChip
									setEquipmentList={equipmentList => this.setState({ equipmentList })}
									canChange={this.props.isPunchIn}
									defaultEquipmentId={this.props.equipmentId}
									useEditablePunchTableDataStore={this.props.useEditablePunchTableDataStore}
								/>
							</div>
						}
					</div>
				</Dialog>
			</div>
		)
	}
}
